import { APP_BASE_HREF, CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { isEqual as _isEqual, last as _last } from 'lodash/fp'
import { MenuItem } from 'primeng/api'
import { MenuModule } from 'primeng/menu'
import { PopoverModule } from 'primeng/popover'

import { Observable, combineLatest, debounceTime } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'

import { FlxButtonModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import { BPMNNodeActionsEvents } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/store/bpmn-node-actions.events'
import { BpmnEditorEffects } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.effects'
import { BpmnEditorSamples } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.samples'
import { BpmnEditorStore } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.store'
import { ProcessState } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { FlxClickStopPropagationDirective } from '@flowx/designer/modules/core/directives/stop-propagation.directive'
import { FlxResourceHeaderComponent } from '@flowx/designer/modules/layout/components/resource-header/resource-header.component'
import { ProcessInstanceInfoComponent } from '@flowx/designer/modules/process/components/process-instance-info/process-instance-info.component'
import { ProcessInstanceReactFlowComponent } from '@flowx/designer/modules/process/components/process-instance-react-flow/process-instance-react-flow.component'
import { DetailsDefaultMenuItem } from '@flowx/designer/modules/projects/components/resources-page/menu-items.constants'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import {
  ApplicationProcessData,
  NewProcessInstance,
  ProcessInstanceBreadcrumb,
} from '@flowx/designer/shared/entities/process-instance.types'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'

import { ProcessInstanceActiveStates } from '../../process.constants'

@Component({
  selector: 'app-process-instance-details',
  templateUrl: './process-instance-details.component.html',
  styleUrls: ['./process-instance-details.component.scss', '../../../../styles/versioning.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FlxResourceHeaderComponent,
    ProcessInstanceReactFlowComponent,
    PopoverModule,
    ProcessInstanceInfoComponent,
    FlxButtonModule,
    FlxIconModule,
    MenuModule,
    FlxClickStopPropagationDirective,
  ],
})
export class ProcessInstanceDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  #bpmnEditorEffects = inject(BpmnEditorEffects)
  #bpmnEditorSamples = inject(BpmnEditorSamples)

  #baseHref = inject(APP_BASE_HREF)
  #destroyRef = inject(DestroyRef)
  #route = inject(ActivatedRoute)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #processInstanceService = inject(ProcessInstanceService)
  #router = inject(Router)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

  applicationProcessData$: Observable<Partial<ApplicationProcessData>> =
    this.#processInstanceService.applicationProcessData
  loading$ = this.#processInstanceService.processInstanceLoading
  context$: Observable<{
    applicationProcessData: Partial<ApplicationProcessData>
    loading: boolean
  }>

  definitionsExist: Signal<boolean> = this.#applicationDetailsStore.definitionExists
  applicationDetailsLoaded = this.#applicationDetailsStore.applicationDetailsLoaded
  appName = this.#applicationDetailsStore.appName
  bpmnEditorStores = inject(BpmnEditorStore)

  breadcrumbItems: MenuItem[] = []
  processInstances = {
    label: 'Processes',
    command: (): void => {
      this.#router.navigate(['runtime/active-instances'], {
        relativeTo: this.#route,
      })
    },
  }

  readonly ProcessState = ProcessState
  accessRights = computed(() => {
    const permissions = this.#workspacesAndPermissionsStore.workspacePermissions.value()
    return {
      canEdit: !!permissions?.wks_process_instance_variables_edit,
    }
  })
  processInstanceMenuItems: MenuItem[] = []

  ngOnInit(): void {
    combineLatest({ queryParamMap: this.#route.queryParamMap, paramMap: this.#route.paramMap })
      .pipe(
        debounceTime(0),
        map(({ queryParamMap, paramMap }) => ({
          uuid: paramMap.get('uuid'),
          embedId: queryParamMap.get('embedId'),
          context: queryParamMap.get('context'),
          breadcrumbs: JSON.parse(queryParamMap.get('breadcrumbs') || '[]'),
        })),
        distinctUntilChanged(_isEqual),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ uuid, embedId, context, breadcrumbs }) => {
        this.#processInstanceService
          .getProcessInstance(uuid, embedId, context)
          .subscribe((applicationProcessData) => {
            this.breadcrumbItems = [
              {
                label: 'Active Instances',
                command: (): void => {
                  this.#router.navigate(['../'], {
                    relativeTo: this.#route,
                  })
                },
              },
              ...this.#buildBreadcrumbs(breadcrumbs),
              { label: applicationProcessData.processData.definition.resourceName },
            ]
            this.processInstanceMenuItems = [
              {
                ...DetailsDefaultMenuItem(
                  !ProcessInstanceActiveStates[applicationProcessData.processData.instance.state],
                  true,
                  false
                ),
                label: 'Modify Variables',
                command: (): Promise<boolean> =>
                  this.#router.navigate([], {
                    queryParams: { tab: 'variables', showEditVariables: 'true' },
                    queryParamsHandling: 'merge',
                  }),
                automationId: 'modify-variables-option',
              },
            ]
          })
      })
  }

  ngAfterViewInit(): void {
    this.context$ = combineLatest({
      applicationProcessData: this.applicationProcessData$,
      loading: this.loading$,
    }).pipe(debounceTime(0))
  }

  goToProcess(
    applicationProcessData: Partial<ApplicationProcessData>,
    clickedNodeId?: number
  ): void {
    let workspaceId: string, applicationId: string, applicationVersionId: string
    const resourceId = applicationProcessData.processData.definition.resourceId
    const embeddedSubprocessInstance = applicationProcessData.processData.embeddedSubprocessInstance

    if (!embeddedSubprocessInstance) {
      const instance = applicationProcessData.processData.instance as unknown as NewProcessInstance
      workspaceId = instance.workspaceId
      applicationId = instance.applicationId
      applicationVersionId = instance.buildApplicationVersionId
    } else {
      workspaceId = embeddedSubprocessInstance.workspaceId
      applicationId = embeddedSubprocessInstance.applicationId
      applicationVersionId = embeddedSubprocessInstance.buildApplicationVersionId
    }

    if (workspaceId && applicationId && applicationVersionId && resourceId) {
      const link = this.#router.serializeUrl(
        this.#router.createUrlTree(
          [
            this.#baseHref,
            'guard',
            'workspace',
            workspaceId,
            'projects',
            applicationId,
            'config',
            applicationVersionId,
            'processes',
            resourceId,
            'diagram',
          ],
          {
            queryParams: {
              nodeId: clickedNodeId,
            },
            relativeTo: null,
          }
        )
      )

      window.open(link, '_blank')
    }
  }

  navigateToSubprocess({
    subprocessUuid,
    name,
    uuid,
    previousEmbedId,
    previousEmbedContext,
    breadcrumbs,
  }: {
    subprocessUuid: string
    name: string
    uuid: string
    previousEmbedId?: string
    previousEmbedContext?: string
    breadcrumbs: ProcessInstanceBreadcrumb[]
  }): void {
    this.#router.navigate(['../', subprocessUuid], {
      queryParams: {
        breadcrumbs: JSON.stringify([
          ...breadcrumbs,
          {
            name,
            uuid,
            previousEmbedContext,
            previousEmbedId,
          },
        ]),
      },
      relativeTo: this.#route,
    })
  }

  navigateToSubprocessEmbed({
    embedId,
    context,
    name,
    uuid,
    previousEmbedId,
    previousEmbedContext,
    breadcrumbs,
  }: {
    embedId: string
    context: string
    name: string
    uuid: string
    previousEmbedId?: string
    previousEmbedContext?: string
    breadcrumbs: ProcessInstanceBreadcrumb[]
  }): void {
    this.#router.navigate(['../', uuid], {
      queryParams: {
        embedId,
        context,
        breadcrumbs: JSON.stringify([
          ...breadcrumbs,
          {
            name,
            uuid,
            previousEmbedContext,
            previousEmbedId,
          },
        ]),
      },
      relativeTo: this.#route,
    })
  }

  backToParentProcess({ breadcrumbs }: { breadcrumbs: ProcessInstanceBreadcrumb[] }): void {
    const lastBreadcrumb = _last(breadcrumbs)

    const processUuid = lastBreadcrumb ? lastBreadcrumb?.uuid : null
    const embedId = lastBreadcrumb ? lastBreadcrumb.previousEmbedId : null
    const context = lastBreadcrumb ? lastBreadcrumb.previousEmbedContext : null

    this.#router.navigate(['../', processUuid], {
      queryParams: {
        embedId,
        context,
        breadcrumbs: JSON.stringify(breadcrumbs.slice(0, -1)),
      },
      relativeTo: this.#route,
    })
  }

  ngOnDestroy(): void {
    this.#processInstanceService.resetInstanceData()
    BPMNNodeActionsEvents.resetProcessInstance()
  }

  toggleMenu(event: MouseEvent, menu: any): void {
    menu.toggle(event)
  }

  #buildBreadcrumbs(breadcrumbs: ProcessInstanceBreadcrumb[]): MenuItem[] {
    return breadcrumbs.map((breadcrumb) => ({
      label: breadcrumb.name,
      command: (event): void => {
        const uuid = breadcrumb.uuid
        const clickedBreadcrumbIndex = breadcrumbs.findIndex(
          (breadcrumb) => breadcrumb.name === event.item.label
        )
        const remainingBreadcrumbs = breadcrumbs.filter(
          (_, index) => index < clickedBreadcrumbIndex
        )

        this.#router.navigate(['../', uuid], {
          queryParams: {
            breadcrumbs: JSON.stringify(remainingBreadcrumbs),
            embedId: breadcrumb.previousEmbedId,
            context: breadcrumb.previousEmbedContext,
          },
          relativeTo: this.#route,
        })
      },
    }))
  }
}
